import { safeFetch, safeAuthFetch } from '../utils/safeFetch';
import {
  authFetch,
  resetSessionExpiredFlag,
  getStoredAuthToken,
  clearStoredAuthToken,
  storeAuthToken,
  isTokenExpired,
  API_BASE,
} from './authFetch.js';

// Re-export for backward compatibility with existing imports.
export { authFetch, resetSessionExpiredFlag };

export class AuthAPI {

  // Bucket C helper: pre-migration these methods parsed response JSON on !ok
  // and threw `new Error(data.error || 'fallback')`. Preserving the same
  // UX contract — the modal/toast surfaces data.error directly.
  static async requestMagicLink(email) {
    const response = await safeFetch(`${API_BASE}/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      allowErrorStatuses: [400, 403, 409, 422, 429],
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to send magic link');
    return data;
  }

  static async verifyMagicLink(token, email) {
    const response = await safeFetch(`${API_BASE}/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, email }),
      allowErrorStatuses: [400, 401, 403, 410, 422],
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Magic link verification failed');
    return data;
  }

  /**
   * Create a guest session for demo purposes (Issue #282).
   * No email required — creates an anonymous temporary account.
   */
  static async createGuestSession(entry = null) {
    // Accepts { audience, scenarioId }; a bare string stays supported.
    const ctx = (entry && typeof entry === 'object') ? entry : { audience: entry };
    const body = {};
    if (ctx.audience) body.audience = ctx.audience;
    // Gemini review catch: coerce first, so a numeric STRING id from a direct
    // caller is not silently dropped. Mirrors the backend's Number() handling.
    const sId = Number(ctx.scenarioId);
    if (Number.isInteger(sId) && sId > 0) body.scenarioId = sId;
    // #3530: where the visitor entered from (lib/entrySource.js). Only when the
    // caller passes one, so the body for every other caller is unchanged. The
    // server re-sanitises it; an absent field is stored as NULL, never `{}`.
    if (ctx.entrySource && typeof ctx.entrySource === 'object') body.entrySource = ctx.entrySource;
    const response = await safeFetch(`${API_BASE}/auth/guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // #2050: the `/for/:audience` front door, so the server can provision this
      // guest into the partner's org instead of the shared trial org. Omitted
      // when unknown — the server then falls back to trial, as it always did.
      body: JSON.stringify(body),
      allowErrorStatuses: [400, 403, 409, 422, 429],
    });
    const data = await response.json();
    if (!response.ok) {
      // #3012: this method used to discard `data.code` and throw a bare
      // Error, so a 429 (GUEST_RATE_LIMITED from `guestRateLimiter` in
      // backend/src/routes/auth.js) was indistinguishable from any other
      // guest-creation failure once it left this function. Callers
      // (AuthContext.loginAsGuest → ScenarioDetailPage's auto-guest catch)
      // need the code to show a specific message and skip the doomed
      // tokenless POST /api/runs that used to follow. Same idiom as
      // captureEmail/inviteUser above.
      const error = new Error(data.error || 'Failed to create demo session');
      error.code = data.code;
      throw error;
    }
    return data;
  }

  /**
   * #2673 — validate a possibly-reusable guest token WITHOUT the global
   * session-expiry side effect `authFetch`/`safeAuthFetch` trigger on a 401.
   *
   * Reusing an existing guest session on landing needs to ask the server
   * "is this token still good?" before committing to it, but a stale/revoked
   * guest token here is an EXPECTED, silent fallback path (mint a fresh
   * guest instead) — not a real session-expiry event. `authFetch` clears the
   * stored token and dispatches `sessionExpired` on any 401 (see
   * services/authFetch.js), which would surface a "your session expired,
   * please log in again" message to a visitor who is about to get a brand
   * new guest session transparently. Using `safeFetch` with an explicit
   * Authorization header (the same manual-header pattern `captureEmail`/
   * `inviteUser` already use below) avoids that dispatch entirely — the
   * caller decides what a 401 means here.
   *
   * @param {string} token
   * @returns {Promise<{ok: boolean, user: object|null}>}
   */
  static async validateGuestSession(token) {
    try {
      const response = await safeFetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        allowErrorStatuses: [401, 403],
      });
      if (!response.ok) return { ok: false, user: null };
      const data = await response.json();
      return { ok: true, user: data.user, voiceBudget: data.voice_budget };
    } catch {
      // #3011: only a 401/403 above is a genuine rejection of the token —
      // `allowErrorStatuses` lets exactly those through as a resolved
      // `!response.ok` rather than a throw. Everything that reaches THIS
      // catch (a cancelled fetch, a network failure, a 5xx, a 429, a
      // malformed body) tells us nothing about whether the token is
      // actually invalid, so it must not be collapsed into the same
      // `{ok:false}` shape a real rejection returns — the caller
      // (`AuthContext.loginAsGuest`) uses `networkError` to tell the two
      // apart and only clears the stored token on a real rejection.
      return { ok: false, user: null, networkError: true };
    }
  }

  /**
   * #3753 — which organization a FRESH guest minted through this entry point
   * ({audience, scenarioId}, the same two fields `createGuestSession` sends)
   * would land in. `AuthContext.loginAsGuest` compares it with the stored
   * guest's org and mints a new guest only when they differ.
   *
   * Never throws. `{ok:false}` means "unknown" (a network failure, a 5xx/503
   * from a failed server-side lookup, a 429, a malformed body): the caller
   * must then keep the guest it has, never mint on an unknown.
   *
   * @param {{audience?: string|null, scenarioId?: number|string|null}} entry
   * @returns {Promise<{ok: true, organizationId: number}|{ok: false}>}
   */
  static async resolveGuestOrganization(entry = {}) {
    try {
      const params = new URLSearchParams();
      if (typeof entry?.audience === 'string' && entry.audience) params.set('audience', entry.audience);
      const sId = Number(entry?.scenarioId);
      if (Number.isInteger(sId) && sId > 0) params.set('scenarioId', String(sId));
      const qs = params.toString();
      const response = await safeFetch(`${API_BASE}/auth/guest/organization${qs ? `?${qs}` : ''}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return { ok: false };
      const data = await response.json();
      const organizationId = Number(data?.organization_id);
      if (!Number.isInteger(organizationId) || organizationId <= 0) return { ok: false };
      return { ok: true, organizationId };
    } catch {
      return { ok: false };
    }
  }

  /**
   * Capture email from guest user (Issue #282, #464).
   * Server emits 4xx with domain-specific error code + voice_budget, which the UI
   * surfaces verbatim. Bucket C: allow the known 4xx statuses through safeFetch,
   * then keep the custom error extraction.
   *
   * @param {string} email
   * @param {string|number|null} [runId] - Issue #1857: optional current-run id
   *   so the magic-link email can deep-link back to it ("Keep this plan").
   *   Both ChatPage callsites (save-progress, and voice-limit since #4257)
   *   pass it. Omit only for a call site unrelated to a specific run.
   */
  static async captureEmail(email, runId = null) {
    const token = AuthAPI.getStoredToken();
    if (!token) throw new Error('Not authenticated');

    const response = await safeFetch(`${API_BASE}/auth/capture-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(runId != null ? { email, runId } : { email }),
      allowErrorStatuses: [400, 401, 402, 403, 409, 422, 429],
    });

    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.error || 'Failed to capture email');
      error.code = data.code;
      error.voiceBudget = data.voice_budget;
      throw error;
    }
    return data;
  }

  /**
   * Sign in with Google OAuth (Issue #299).
   * @param {string} credential - Google ID token from Google Sign-In
   * @param {Object} [options]
   * @param {'yes'|'no'} [options.guestConversionAnswer] - D-121 (#2725): the
   *   learner's answer to "is this unfinished session yours?", sent on the
   *   SECOND call once AuthContext.loginWithGoogle has shown that prompt.
   *   Omitted on the first call.
   * @param {string} [options.guestConversionToken] - review-fix (P1 codex,
   *   #2725): echoes back the FIRST call's
   *   `guest_conversion_pending.confirm_token`, proving this second call is
   *   answering that specific ask rather than a caller that skipped
   *   straight to guestConversionAnswer:'yes'. Omitted on the first call.
   * @param {string|null} [options.bearerToken] - #3475 Q2881-4: the token to
   *   nominate the guest with. AuthContext.loginWithGoogle captures it ONCE,
   *   before the first call, and passes the same value on every call of the
   *   confirm round trip. When the option is absent, the stored token is read
   *   here (the single-call shape every other caller has).
   */
  static async loginWithGoogle(credential, { guestConversionAnswer, guestConversionToken, bearerToken } = {}) {
    // #2704: carry the caller's guest token so the backend can convert an
    // in-flight guest session instead of creating a second account. This is
    // the ONLY handle on a guest who never captured an email — without it the
    // backend can match on `pending_email` alone, which covers the noisy
    // (500-producing) cases and misses the larger silent one. A non-guest
    // token here is harmless: the backend ignores anything not `is_guest`.
    //
    // #2725 read the token fresh on EVERY call so the second call re-sent the
    // same guest token. That holds in one tab, but `window.confirm()` blocks
    // only its own tab: another tab can write a different token while the
    // prompt is open, and the answer would then be applied to a guest the
    // learner never saw (#3475 Q2881-4). The caller now captures the token
    // once and passes it explicitly; in the single-tab case the captured
    // value IS what a fresh read would return, so that case is unchanged.
    // AuthContext compares storage against the captured value before each
    // later call and stops if it changed.
    const nominated = bearerToken !== undefined ? bearerToken : AuthAPI.getStoredToken();
    const headers = { 'Content-Type': 'application/json' };
    if (nominated) headers.Authorization = `Bearer ${nominated}`;

    const body = { credential };
    if (guestConversionAnswer) body.guestConversionAnswer = guestConversionAnswer;
    if (guestConversionToken) body.guestConversionToken = guestConversionToken;

    const response = await safeFetch(`${API_BASE}/auth/google`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      // 409: ACCOUNT_CONFLICT and, since #3487 Q2881-6, ACCOUNT_ORG_CONFLICT —
      // previously thrown as a bare FetchError ("something went wrong").
      allowErrorStatuses: [400, 401, 403, 409, 422],
    });
    const data = await response.json();
    if (!response.ok) {
      // Keep the backend's code (mega115 plan check finding 5): callers and
      // toUserFacingMessage map codes to a translated, actionable message.
      const error = new Error(data.error || 'Google sign-in failed');
      error.code = data.code;
      error.status = response.status;
      throw error;
    }
    return data;
  }

  // #3578 plan check design change 4: `signal` lets AuthContext's restore
  // cancel a request it has stopped waiting for (its bounded timeout).
  static async getCurrentUser({ signal } = {}) {
    const token = AuthAPI.getStoredToken();
    if (!token) throw new Error('No authentication token found');

    const response = await safeAuthFetch(`${API_BASE}/auth/me`, signal ? { signal } : {});
    return response.json();
  }

  /**
   * Invite a user by email and role (Issue #325).
   * Bucket C: error body carries a `code` field that the UI surfaces.
   */
  static async inviteUser(email, role) {
    const token = AuthAPI.getStoredToken();
    if (!token) throw new Error('Not authenticated');

    const response = await safeFetch(`${API_BASE}/auth/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email, role }),
      allowErrorStatuses: [400, 401, 403, 409, 422],
    });

    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.error || 'Failed to send invitation');
      error.code = data.code;
      throw error;
    }
    return data;
  }

  /**
   * Bulk invite facilitators (Issue #810).
   * @param {Array<{email: string, name?: string}>} entries
   * @param {{ sendWelcomeEmail?: boolean }} options
   * @returns {Promise<{results: Array, summary: Object}>}
   */
  static async bulkInviteFacilitators(entries, { sendWelcomeEmail = true } = {}) {
    const token = AuthAPI.getStoredToken();
    if (!token) throw new Error('Not authenticated');

    // Bucket C: 4xx carries a domain-specific message (e.g. "3 addresses invalid").
    const response = await safeFetch(`${API_BASE}/auth/bulk-invite-facilitators`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ entries, sendWelcomeEmail }),
      allowErrorStatuses: [400, 401, 403, 409, 422],
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Bulk invite failed');
    return data;
  }

  /**
   * Update the current user's display name (Issue #719).
   */
  static async updateName(name) {
    const response = await safeAuthFetch(`${API_BASE}/auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
      allowErrorStatuses: [400, 403, 409, 422],
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to update name');
    return data;
  }

  /**
   * Mark the Highlights/briefing tab as seen for the current user (Issue #1110).
   * Server overwrites users.last_briefing_seen_at = NOW() and returns
   * { prev_seen_at, seen_at }. The caller is expected to have already read
   * prev_seen_at from /auth/me (snapshotted at PeriodHeader mount).
   */
  static async markBriefingSeen() {
    const response = await safeAuthFetch(`${API_BASE}/auth/me/mark-briefing-seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`POST /auth/me/mark-briefing-seen returned ${response.status}`);
    }
    return response.json();
  }

  // #2684: returns a structured result (rather than nothing) so the caller
  // (AuthContext.logout) can tell a real server-side revocation apart from a
  // failed one, instead of the two being indistinguishable no-ops. Local
  // token clearing below is UNCONDITIONAL either way — a server-side failure
  // must never trap a learner in a logged-in UI (see AuthContext.jsx).
  static async logout() {
    const token = AuthAPI.getStoredToken();
    let result = { ok: true, status: null }; // no stored token: nothing to revoke server-side
    if (token) {
      try {
        const response = await safeFetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        result = { ok: true, status: response.status };
      } catch (error) {
        // safeFetch throws a FetchError (with a numeric `.status`, 0 for a
        // network failure) on any non-2xx response or transport error — see
        // frontend/src/utils/safeFetch.ts. Before #2684 this branch was the
        // ONLY place a guest's blocked-by-allowlist 403 was ever observed,
        // and it logged a bare, untyped message with no status field, so a
        // real server-side revocation failure was indistinguishable from
        // "nothing happened" in the logs. `[AUTH_LOGOUT_FAILED]` makes it
        // greppable; the status is a named field, not buried in a message.
        const status = typeof error?.status === 'number' ? error.status : null;
        console.warn('[AUTH_LOGOUT_FAILED] Server logout call did not succeed; clearing local session anyway', {
          status,
          message: error?.message,
        });
        result = { ok: false, status };
      }
    }
    // #3580 sibling (7b): compare-and-swap. The revoke above is a network
    // round trip; if another tab signed in and wrote a NEWER token meanwhile,
    // clearing unconditionally would sign that session out too, although
    // nobody logged it out. Clear only the token this logout revoked (or an
    // empty slot, a no-op). The {ok, status} result shape is unchanged.
    if (AuthAPI.getStoredToken() === token) {
      AuthAPI.clearStoredToken();
    }
    return result;
  }

  // Token storage helpers. Throws STORAGE_UNAVAILABLE when the browser blocks
  // storage (#3578; see storeAuthToken).
  static storeToken(token) {
    storeAuthToken(token);
  }

  static getStoredToken() {
    return getStoredAuthToken();
  }

  static clearStoredToken() {
    clearStoredAuthToken();
  }

  static isAuthenticated() {
    return !!this.getStoredToken();
  }

  static isTokenExpired(token) {
    return isTokenExpired(token);
  }

  static getToken() {
    return this.getStoredToken();
  }

  static getAuthHeaders() {
    const token = this.getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}

export default AuthAPI;
